import { useState, useRef } from 'react';
import { atom, useAtom } from 'jotai';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MDSIcon, MDSSnackbar } from '@marqvision/mds-v2';
import { useTranslateMutation } from '~/apis/common';
import { useUserInfoQuery } from '~/apis/auth';
import { MarqCommonEnumsTranslateStatusEnum } from '~/swagger/data-contracts';
import { useLogger } from '~/hooks';
import { amplitudeChannelMap, amplitudeSubTabMap, RouteKey, SubTabKey } from '~/types';

const shouldDisplayTranslationHighlight = atom(false);

export const useTranslate = () => {
  const { amplitudeLogging } = useLogger();

  const [isHighlightOn, setIsHighlightOn] = useAtom(shouldDisplayTranslationHighlight);
  const [shouldDisplayTooltip, setShouldDisplayTooltip] = useState(true);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const [searchParams] = useSearchParams();
  const sellerDetailListingTab = searchParams.get('seller_detail_listing_tab');
  const channel = pathSegments[0];
  const subTab = pathSegments[1];

  const { data, isLoading: isUserInfoQueryLoading } = useUserInfoQuery();
  const { mutateAsync: translateMutateAsync, isLoading: isTranslateMutateLoading } = useTranslateMutation();

  const isRequestLoading = isUserInfoQueryLoading || isTranslateMutateLoading;
  const isTranslationToggleActive = data?.use_translate === MarqCommonEnumsTranslateStatusEnum.On;
  const newTranslateStatus = isTranslationToggleActive
    ? MarqCommonEnumsTranslateStatusEnum.Off
    : MarqCommonEnumsTranslateStatusEnum.On;

  const toggleTranslationStatus = async () => {
    const toggleStatus = newTranslateStatus === MarqCommonEnumsTranslateStatusEnum.Off ? 'Off' : 'On';

    setShouldDisplayTooltip(toggleStatus !== 'On');

    await translateMutateAsync({ translate: newTranslateStatus });

    const highlightState = newTranslateStatus === MarqCommonEnumsTranslateStatusEnum.On;
    setIsHighlightOn(highlightState);

    if (highlightState) {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = setTimeout(() => {
        setIsHighlightOn(false);
        highlightTimeoutRef.current = null;
      }, 3000);
    }

    MDSSnackbar({
      type: 'complete',
      title: `Language translation turned ${toggleStatus}`,
      duration: 5000,
      customIcon: <MDSIcon.Translate color="color/content/inverse/default/normal" />,
    });

    amplitudeLogging('clicked translation', {
      Channel: amplitudeChannelMap[channel as RouteKey],
      Status_tab: amplitudeSubTabMap[sellerDetailListingTab as SubTabKey] || amplitudeSubTabMap[subTab as SubTabKey],
      Translation_status: newTranslateStatus,
    });
  };

  const handleTranslationOff = async () => {
    if (!isTranslationToggleActive) return;

    await translateMutateAsync({ translate: MarqCommonEnumsTranslateStatusEnum.Off });

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }

    setShouldDisplayTooltip(false);
    setIsHighlightOn(false);

    MDSSnackbar({
      type: 'complete',
      title: 'Language translation turned off (Search is only supported in the original language)',
      duration: 5000,
      customIcon: <MDSIcon.Translate color="color/content/inverse/default/normal" />,
    });
  };

  return {
    isHighlightOn,
    isRequestLoading,
    newTranslateStatus,
    shouldDisplayTooltip,
    isTranslationToggleActive,
    loggingChannel: amplitudeChannelMap[channel as RouteKey],
    loggingSubTab: amplitudeSubTabMap[sellerDetailListingTab as SubTabKey] || amplitudeSubTabMap[subTab as SubTabKey],
    onTranslateToggle: toggleTranslationStatus,
    onTranslationOff: handleTranslationOff,
  };
};
